import React from 'react';
import { Link } from 'react-router-dom';
import SmartImg from './SmartImg';

/**
 * Shared tile component used across every listing/discovery surface
 * (My Library, My Giveaways, My Projects, Discover feed, Friends tabs,
 * Community tabs, Profile, Giveaways page).
 *
 * Anatomy:
 *   ┌──────────────────────────────────────────┐
 *   │ header  (title + optional topBadge)       │  ← shrink-wrap, min-h 52
 *   ├──────────────────────────────────────────┤
 *   │ ┌────────┐                                │
 *   │ │ photo  │  body (description + meta …)  │  ← split row, flex-1
 *   │ └────────┘                                │
 *   ├──────────────────────────────────────────┤
 *   │ footer (View CTA + actions)               │  ← auto-stick to bottom
 *   └──────────────────────────────────────────┘
 *
 * Slots:
 *   - header       (ReactNode, required) — renders inside the top bar
 *   - photo        (string URL, optional) — when empty, the image column
 *                  is removed entirely and body fills the full width
 *   - placeholderIcon (ReactNode, optional) — shown INSIDE the image column
 *                  when `photo` is falsy but you still want to reserve
 *                  the left thumbnail slot (e.g. Projects page keeps a
 *                  wrench placeholder for visual rhythm)
 *   - body         (ReactNode, required) — right-hand description + meta
 *   - footer       (ReactNode, required) — action row at the bottom
 *
 * Cosmetic knobs:
 *   - minHeight: "260px" | "280px" | custom
 *   - borderClass: override the default border (e.g. urgent red tint)
 *   - onPhotoClick: handler for the image (e.g. toggle-select)
 *   - photoHref:   when provided, the image column becomes a router Link
 *                  to this path (keeps right-click / open-in-new-tab). Takes
 *                  precedence over onPhotoClick.
 */
export default function EntityTile({
  header,
  photo,
  placeholderIcon,
  onPhotoClick,
  photoHref,
  body,
  footer,
  minHeight = 260,
  borderClass = 'border-white/50 hover:shadow-md hover:border-white/70',
  className = '',
  testId,
}) {
  const showPhotoColumn = !!photo || !!placeholderIcon;
  const photoInner = (
    <SmartImg
      src={photo}
      alt=""
      className="w-full h-full object-cover"
      fallbackClassName="w-full h-full"
      fallback={placeholderIcon}
    />
  );
  const photoBaseClass = 'w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-stone-100 relative block';
  return (
    <div
      data-testid={testId}
      style={{ minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight }}
      className={`glass-card rounded-xl border overflow-hidden transition-all flex flex-col ${borderClass} ${className}`}
    >
      {/* Header */}
      <div className="px-3 pt-3 pb-2 border-b border-stone-100/70 flex items-start gap-2 min-h-[52px]">
        {header}
      </div>

      {/* Split row */}
      <div className="flex gap-3 px-3 py-3 flex-1 min-h-0">
        {showPhotoColumn && (
          photoHref ? (
            <Link
              to={photoHref}
              aria-label="Open item details"
              className={`${photoBaseClass} cursor-pointer hover:opacity-90 transition-opacity`}
            >
              {photoInner}
            </Link>
          ) : (
            <div
              onClick={onPhotoClick}
              className={`${photoBaseClass} ${onPhotoClick ? 'cursor-pointer' : ''}`}
            >
              {photoInner}
            </div>
          )
        )}
        <div className="flex-1 min-w-0 flex flex-col gap-1.5 overflow-hidden text-left">
          {body}
        </div>
      </div>

      {/* Footer */}
      {footer && (
        <div className="flex gap-1.5 px-3 pb-3 pt-2 mt-auto border-t border-stone-100/70">
          {footer}
        </div>
      )}
    </div>
  );
}

/** Common tag/pill used inside the body slot so every page renders them
 * identically. Keep this small — complex pages can still inline custom
 * spans alongside this. */
export function TilePill({ children, variant = 'gray' }) {
  const variants = {
    gray:  'bg-gray-100 text-gray-500',
    green: 'bg-green-50 text-[#004B00] border border-green-200',
    blue:  'bg-blue-100 text-blue-700',
    amber: 'bg-amber-100 text-amber-700',
    purple:'bg-purple-100 text-purple-700',
    pink:  'bg-pink-100 text-pink-600',
    stone: 'bg-stone-100 text-gray-600',
  };
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full font-nunito font-medium whitespace-nowrap ${variants[variant] || variants.gray}`}>
      {children}
    </span>
  );
}

/** A tiny "View" button shared by every footer. */
export function TileViewButton({ to, label = 'View', testId, variant = 'solid' }) {
  const styles = variant === 'solid'
    ? 'bg-[#004B00] text-white hover:bg-[#003800]'
    : 'border border-[#004B00] text-[#004B00] hover:bg-green-50';
  // Rendered as a plain <a> so this component stays router-agnostic.
  // Pages using react-router can wrap with <Link> instead.
  return (
    <a href={to} data-testid={testId}
      className={`flex-1 text-center text-xs py-2 rounded-lg font-nunito font-semibold transition-all ${styles}`}>
      {label}
    </a>
  );
}
